/**
 * IRGenerator — Converts a visual graph into structured IR with control flow.
 *
 * One IRBlock per lifecycle event. Supports:
 * - Linear instruction sequences
 * - Branching (if/else)
 * - Loops (for, while)
 * - Nested control flow
 */

import type {
  CompileDiagnostic,
  IRBlock,
  IRFor,
  IRIf,
  IRInstruction,
  IRProgram,
  IRStatement,
  IRWhile,
  NodeBehaviour,
  NodeCompileContext,
  NodeInstance,
  PortDataType,
} from '../types';
import { Graph } from '../graph/base';
import { NodeRegistry } from '../node/NodeRegistry';
import { defaultLiteralFor, toLiteralString } from '../node/PortTypes';

// ============================================================================
// Event → lifecycle method mapping
// ============================================================================

/** Maps event node type IDs to lifecycle method names */
const EVENT_METHOD_MAP: Record<string, string> = {
  'events.update': 'update',
  'events.fixed_update': 'fixedUpdate',
  'events.late_update': 'lateUpdate',
  'events.awake': 'onAwake',
  'events.start': 'onStart',
  'events.enable': 'onEnable',
  'events.disable': 'onDisable',
  'events.destroy': 'onDestroy',
};

/**
 * Get the lifecycle method name for an event node type.
 */
export function eventToMethod(eventTypeId: string): string {
  return EVENT_METHOD_MAP[eventTypeId] ?? eventTypeId.split('.').pop()!;
}

// ============================================================================
// Control flow node types
// ============================================================================

const BRANCH_NODES = new Set(['flow.branch']);
const FOR_LOOP_NODES = new Set(['flow.forLoop']);
const WHILE_LOOP_NODES = new Set(['flow.whileLoop']);
const SEQUENCE_NODES = new Set(['flow.sequence']);

// ============================================================================
// IRGenerator
// ============================================================================

export class IRGenerator {
  private _tempCounter = 0;
  private _instrCounter = 0;
  private _diagnostics: CompileDiagnostic[] = [];
  private _resultCache = new Map<string, string>();

  constructor(
    private readonly _graph: Graph,
    private readonly _registry: NodeRegistry,
  ) {}

  /**
   * Generate the full IR program from the graph.
   */
  generate(): { program: IRProgram; diagnostics: CompileDiagnostic[] } {
    this._tempCounter = 0;
    this._instrCounter = 0;
    this._diagnostics = [];
    this._resultCache.clear();

    const blocks: IRBlock[] = [];

    // Find all event nodes → each becomes a block
    for (const node of this._graph.getNodes()) {
      const def = this._registry.get(node.type);
      if (!def?.isEvent) continue;

      const methodName = eventToMethod(node.type);
      const statements = this._generateEventBlock(node);

      blocks.push({ name: methodName, statements });
    }

    // Fields from variables
    const fields = this._graph.getVariables().map(v => ({
      name: v.name,
      type: v.type,
      defaultValue: v.defaultValue,
      exposed: v.exposed,
    }));

    const program: IRProgram = {
      name: this._sanitizeName(this._graph.name),
      fields,
      blocks,
    };

    return { program, diagnostics: this._diagnostics };
  }

  // --------------------------------------------------------------------------
  // Event block generation
  // --------------------------------------------------------------------------

  /**
   * Generate statements for an event node's execution chain.
   */
  private _generateEventBlock(eventNode: NodeInstance): IRStatement[] {
    const statements: IRStatement[] = [];

    // Emit parameter instructions for event outputs (e.g., dt for update)
    const eventDef = this._registry.get(eventNode.type);
    if (eventDef) {
      for (const outPort of eventDef.outputs) {
        if (outPort.type !== 'flow') {
          const id = this._nextInstrId();
          statements.push(this._instr(id, `event_param:${outPort.id}`, [], outPort.type, eventNode.id));
          this._setNodeResult(eventNode.id, outPort.id, id);
        }
      }
    }

    // Follow the primary flow output
    const flowOut = eventDef?.outputs.find(p => p.type === 'flow');
    if (flowOut) {
      const edges = this._graph.getOutputEdges(eventNode.id, flowOut.id);
      for (const edge of edges) {
        const nextNode = this._graph.getNode(edge.to.nodeId);
        if (nextNode) {
          this._walkFlow(nextNode, statements, new Set());
        }
      }
    }

    return statements;
  }

  // --------------------------------------------------------------------------
  // Flow traversal with control flow support
  // --------------------------------------------------------------------------

  /**
   * Walk the flow graph, generating statements.
   * Handles linear flow, branches, and loops.
   */
  private _walkFlow(
    node: NodeInstance,
    statements: IRStatement[],
    visited: Set<string>,
  ): void {
    if (visited.has(node.id)) return;
    visited.add(node.id);

    const def = this._registry.get(node.type);
    if (!def) return;

    // Handle different node types
    if (BRANCH_NODES.has(node.type)) {
      this._emitBranch(node, def, statements, visited);
    } else if (FOR_LOOP_NODES.has(node.type)) {
      this._emitForLoop(node, def, statements, visited);
    } else if (WHILE_LOOP_NODES.has(node.type)) {
      this._emitWhileLoop(node, def, statements, visited);
    } else if (SEQUENCE_NODES.has(node.type)) {
      this._emitSequence(node, def, statements, visited);
    } else {
      // Regular node — evaluate and continue
      this._evaluateNode(node, statements, new Set());
      this._continueFlow(node, def, statements, visited);
    }
  }

  /**
   * Continue flow from a node's flow outputs.
   */
  private _continueFlow(
    node: NodeInstance,
    def: NodeBehaviour,
    statements: IRStatement[],
    visited: Set<string>,
    specificPort?: string,
  ): void {
    const flowOutputs = specificPort
      ? def.outputs.filter(p => p.type === 'flow' && p.id === specificPort)
      : def.outputs.filter(p => p.type === 'flow');

    for (const flowPort of flowOutputs) {
      const edges = this._graph.getOutputEdges(node.id, flowPort.id);
      for (const edge of edges) {
        const nextNode = this._graph.getNode(edge.to.nodeId);
        if (nextNode) {
          this._walkFlow(nextNode, statements, new Set(visited));
        }
      }
    }
  }

  // --------------------------------------------------------------------------
  // Branch (if/else)
  // --------------------------------------------------------------------------

  private _emitBranch(
    node: NodeInstance,
    def: NodeBehaviour,
    statements: IRStatement[],
    visited: Set<string>,
  ): void {
    // Resolve condition — first evaluate dependencies
    this._resolveInput(node, 'condition', statements, new Set());
    const condition = this._resolveInputValue(node, 'condition', def);

    // Generate then block
    const thenBody: IRStatement[] = [];
    const trueEdges = this._graph.getOutputEdges(node.id, 'true');
    for (const edge of trueEdges) {
      const nextNode = this._graph.getNode(edge.to.nodeId);
      if (nextNode) {
        this._walkFlow(nextNode, thenBody, new Set(visited));
      }
    }

    // Generate else block
    const elseBody: IRStatement[] = [];
    const falseEdges = this._graph.getOutputEdges(node.id, 'false');
    for (const edge of falseEdges) {
      const nextNode = this._graph.getNode(edge.to.nodeId);
      if (nextNode) {
        this._walkFlow(nextNode, elseBody, new Set(visited));
      }
    }

    const irIf: IRIf = {
      kind: 'if',
      condition,
      thenBody,
      elseBody: elseBody.length > 0 ? elseBody : undefined,
      sourceNodeId: node.id,
    };

    statements.push(irIf);
  }

  // --------------------------------------------------------------------------
  // For Loop
  // --------------------------------------------------------------------------

  private _emitForLoop(
    node: NodeInstance,
    def: NodeBehaviour,
    statements: IRStatement[],
    visited: Set<string>,
  ): void {
    // Resolve start/end — evaluate dependencies first
    this._resolveInput(node, 'start', statements, new Set());
    this._resolveInput(node, 'end', statements, new Set());

    const start = this._resolveInputValue(node, 'start', def);
    const end = this._resolveInputValue(node, 'end', def);
    const indexVar = `_idx_${this._tempCounter++}`;

    // Cache the index variable for use in the loop body
    this._setNodeResult(node.id, 'index', `$${indexVar}`);

    // Generate loop body
    const body: IRStatement[] = [];
    const bodyEdges = this._graph.getOutputEdges(node.id, 'body');
    for (const edge of bodyEdges) {
      const nextNode = this._graph.getNode(edge.to.nodeId);
      if (nextNode) {
        this._walkFlow(nextNode, body, new Set(visited));
      }
    }

    const irFor: IRFor = {
      kind: 'for',
      indexVar,
      start,
      end,
      body,
      sourceNodeId: node.id,
    };

    statements.push(irFor);

    // Continue from 'done' output after the loop
    const doneEdges = this._graph.getOutputEdges(node.id, 'done');
    for (const edge of doneEdges) {
      const nextNode = this._graph.getNode(edge.to.nodeId);
      if (nextNode) {
        this._walkFlow(nextNode, statements, new Set(visited));
      }
    }
  }

  // --------------------------------------------------------------------------
  // While Loop
  // --------------------------------------------------------------------------

  private _emitWhileLoop(
    node: NodeInstance,
    def: NodeBehaviour,
    statements: IRStatement[],
    visited: Set<string>,
  ): void {
    // Resolve condition dependencies
    this._resolveInput(node, 'condition', statements, new Set());
    const condition = this._resolveInputValue(node, 'condition', def);

    // Generate loop body
    const body: IRStatement[] = [];
    const bodyEdges = this._graph.getOutputEdges(node.id, 'body');
    for (const edge of bodyEdges) {
      const nextNode = this._graph.getNode(edge.to.nodeId);
      if (nextNode) {
        this._walkFlow(nextNode, body, new Set(visited));
      }
    }

    const irWhile: IRWhile = {
      kind: 'while',
      condition,
      body,
      sourceNodeId: node.id,
    };

    statements.push(irWhile);

    // Continue from 'done' output after the loop
    const doneEdges = this._graph.getOutputEdges(node.id, 'done');
    for (const edge of doneEdges) {
      const nextNode = this._graph.getNode(edge.to.nodeId);
      if (nextNode) {
        this._walkFlow(nextNode, statements, new Set(visited));
      }
    }
  }

  // --------------------------------------------------------------------------
  // Sequence
  // --------------------------------------------------------------------------

  private _emitSequence(
    node: NodeInstance,
    def: NodeBehaviour,
    statements: IRStatement[],
    _visited: Set<string>,
  ): void {
    // Execute each output in order
    const flowOutputs = def.outputs.filter(p => p.type === 'flow');
    for (const flowPort of flowOutputs) {
      const edges = this._graph.getOutputEdges(node.id, flowPort.id);
      for (const edge of edges) {
        const nextNode = this._graph.getNode(edge.to.nodeId);
        if (nextNode) {
          // Each sequence output gets a fresh visited set
          this._walkFlow(nextNode, statements, new Set());
        }
      }
    }
  }

  // --------------------------------------------------------------------------
  // Node evaluation
  // --------------------------------------------------------------------------

  /**
   * Evaluate a node and emit its instructions.
   */
  private _evaluateNode(
    node: NodeInstance,
    statements: IRStatement[],
    evaluating: Set<string>,
  ): void {
    const def = this._registry.get(node.type);
    if (!def) return;

    // Check if already fully evaluated
    const allCached = def.outputs
      .filter(p => p.type !== 'flow')
      .every(p => this._getNodeResult(node.id, p.id) !== undefined);
    if (allCached && def.isPure) return;

    // Cycle detection
    if (evaluating.has(node.id)) {
      this._diagnostics.push({
        severity: 'error',
        message: `Circular data dependency on node '${def.name}'`,
        nodeId: node.id,
      });
      return;
    }
    evaluating.add(node.id);

    // Resolve data inputs first
    for (const inputPort of def.inputs) {
      if (inputPort.type === 'flow') continue;
      this._resolveInput(node, inputPort.id, statements, evaluating);
    }

    // Compile the node
    if (def.compile) {
      const ctx = this._createCompileContext(node, def, statements);
      const result = def.compile(ctx);
      if (result && def.outputs.length > 0) {
        const primaryOut = def.outputs.find(p => p.type !== 'flow');
        if (primaryOut) this._setNodeResult(node.id, primaryOut.id, result);
      }
    } else {
      this._compileGeneric(node, def, statements);
    }

    evaluating.delete(node.id);
  }

  // --------------------------------------------------------------------------
  // Input resolution
  // --------------------------------------------------------------------------

  /**
   * Resolve an input port's value, evaluating source nodes as needed.
   */
  private _resolveInput(
    node: NodeInstance,
    portId: string,
    statements: IRStatement[],
    evaluating: Set<string>,
  ): string {
    const edge = this._graph.getInputEdge(node.id, portId);

    if (edge) {
      const srcNode = this._graph.getNode(edge.from.nodeId);
      if (!srcNode) return this._emitLiteral(node, portId, statements);

      let cached = this._getNodeResult(srcNode.id, edge.from.portId);
      if (!cached) {
        this._evaluateNode(srcNode, statements, evaluating);
        cached = this._getNodeResult(srcNode.id, edge.from.portId);
      }

      return cached ?? this._emitLiteral(node, portId, statements);
    }

    return this._emitLiteral(node, portId, statements);
  }

  /**
   * Get input value as a reference string (without emitting instructions).
   */
  private _resolveInputValue(
    node: NodeInstance,
    portId: string,
    def: NodeBehaviour,
  ): string {
    const edge = this._graph.getInputEdge(node.id, portId);

    if (edge) {
      const cached = this._getNodeResult(edge.from.nodeId, edge.from.portId);
      if (cached) return cached;
    }

    // Return literal
    const portDef = def.inputs.find(p => p.id === portId);
    const type = portDef?.type ?? 'f32';
    const value = node.inputValues[portId] ?? portDef?.defaultValue;
    const literal = value !== undefined ? toLiteralString(type, value) : defaultLiteralFor(type);
    return `#${literal}`;
  }

  /**
   * Emit a literal value instruction.
   */
  private _emitLiteral(
    node: NodeInstance,
    portId: string,
    statements: IRStatement[],
  ): string {
    const def = this._registry.get(node.type);
    const portDef = def?.inputs.find(p => p.id === portId);
    const type = portDef?.type ?? 'f32';
    const value = node.inputValues[portId] ?? portDef?.defaultValue;
    const literal = value !== undefined ? toLiteralString(type, value) : defaultLiteralFor(type);

    const id = this._nextInstrId();
    statements.push(this._instr(id, 'literal', [`#${literal}`], type, node.id));

    return id;
  }

  // --------------------------------------------------------------------------
  // Compile context
  // --------------------------------------------------------------------------

  private _createCompileContext(
    node: NodeInstance,
    def: NodeBehaviour,
    statements: IRStatement[],
  ): NodeCompileContext {
    return {
      resolveInput: (portId: string): string => {
        const edge = this._graph.getInputEdge(node.id, portId);
        if (edge) {
          const cached = this._getNodeResult(edge.from.nodeId, edge.from.portId);
          if (cached) return cached;
        }
        const portDef = def.inputs.find(p => p.id === portId);
        const type = portDef?.type ?? 'f32';
        const value = node.inputValues[portId] ?? portDef?.defaultValue;
        const literal = value !== undefined ? toLiteralString(type, value) : defaultLiteralFor(type);
        return `#${literal}`;
      },

      emit: (op: string, inputs: string[], resultType: PortDataType, sideEffects?: boolean): string => {
        const id = this._nextInstrId();
        statements.push(this._instr(id, op, inputs, resultType, node.id, sideEffects));
        const primaryOut = def.outputs.find(p => p.type !== 'flow');
        if (primaryOut) this._setNodeResult(node.id, primaryOut.id, id);
        return id;
      },

      tempVar: (prefix = 't'): string => `_${prefix}_${this._tempCounter++}`,

      variable: (name: string): string => `$${name}`,
    };
  }

  // --------------------------------------------------------------------------
  // Generic compilation
  // --------------------------------------------------------------------------

  private _compileGeneric(
    node: NodeInstance,
    def: NodeBehaviour,
    statements: IRStatement[],
  ): void {
    const inputRefs: string[] = [];
    for (const port of def.inputs) {
      if (port.type === 'flow') continue;
      const edge = this._graph.getInputEdge(node.id, port.id);
      if (edge) {
        const cached = this._getNodeResult(edge.from.nodeId, edge.from.portId);
        inputRefs.push(cached ?? `#${defaultLiteralFor(port.type)}`);
      } else {
        const value = node.inputValues[port.id] ?? port.defaultValue;
        const literal = value !== undefined ? toLiteralString(port.type, value) : defaultLiteralFor(port.type);
        inputRefs.push(`#${literal}`);
      }
    }

    const primaryOut = def.outputs.find(p => p.type !== 'flow');
    const hasFlowIn = def.inputs.some(p => p.type === 'flow');
    const hasSideEffects = !def.isPure || hasFlowIn;

    if (primaryOut) {
      // Node with data output → emit instruction and cache result
      const id = this._nextInstrId();
      statements.push(this._instr(id, `node:${node.type}`, inputRefs, primaryOut.type, node.id, hasSideEffects));
      this._setNodeResult(node.id, primaryOut.id, id);
    } else if (hasSideEffects) {
      // Action node with no data output but has side effects → emit void instruction
      const id = this._nextInstrId();
      statements.push(this._instr(id, `node:${node.type}`, inputRefs, 'void', node.id, true));
    }
  }

  // --------------------------------------------------------------------------
  // Helpers
  // --------------------------------------------------------------------------

  private _instr(
    id: string,
    op: string,
    inputs: string[],
    resultType: PortDataType,
    sourceNodeId?: string,
    hasSideEffects?: boolean,
  ): IRInstruction {
    return { kind: 'instruction', id, op, inputs, resultType, sourceNodeId, hasSideEffects };
  }

  private _nextInstrId(): string {
    return `i${this._instrCounter++}`;
  }

  private _setNodeResult(nodeId: string, portId: string, instrId: string): void {
    this._resultCache.set(`${nodeId}:${portId}`, instrId);
  }

  private _getNodeResult(nodeId: string, portId: string): string | undefined {
    return this._resultCache.get(`${nodeId}:${portId}`);
  }

  private _sanitizeName(name: string): string {
    return name
      .replace(/[^a-zA-Z0-9_]/g, '_')
      .replace(/^(\d)/, '_$1')
      .replace(/_+/g, '_') || 'RedprintGraph';
  }
}
